import {
  CSSProperties,
  ChangeEvent,
  FocusEvent,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  MouseEvent,
  ReactNode,
  useRef,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { cn } from "../ClassName";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

interface TableInputProps {
  id?: string;
  min?: string | number;
  type?: HTMLInputTypeAttribute;
  className?: string;
  value?: string | number;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  placeholder?: string;
  overrideStyle?: CSSProperties;
  dataTestId?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  children?: ReactNode;
  innerRef?: React.RefObject<HTMLInputElement>;
  maxLength?: number;
  disabled?: boolean;
  permanentPlaceholder?: string;
  hideNumberInputArrows?: boolean;
}

/**
 * Input component to be used in table forms
 * Can validate values
 * @param {string} id - The id of the input
 * @param {string | number} min - The minimum value of the input
 * @param {HTMLInputTypeAttribute} type - The type of the input
 * @param {string} className - The class name of the input
 * @param {string | number} value - The value of the input
 * @param {function} onClick - The function to be called when the input is clicked
 * @param {function} onChange - The function to be called when the input changes
 * @param {function} onFocus - The function to be called when the input is focused
 * @param {function} onBlur - The function to be called when the input is blurred
 * @param {InputHTMLAttributes<HTMLInputElement>["autoComplete"]} autoComplete - The autocomplete of the input
 * @param {string} placeholder - The placeholder of the input
 * @param {CSSProperties} overrideStyle - The override style of the input
 * @param {string} dataTestId - The data test id of the input
 * @param {boolean} ariaInvalid - Marks the input invalid for screen readers
 * @param {string} ariaDescribedBy - The id of the element describing the input, e.g. its error
 * @param {ReactNode} children - The children of the input
 * @param {React.RefObject<HTMLInputElement>} innerRef - The inner ref of the input
 * @param {number} maxLength - The maximum length of the input
 * @param {boolean} disabled - The disabled state of the input
 * @param {string} permanentPlaceholder - The permanent placeholder of the input (displayed on the right side, stays when input is typed)
 * @param {boolean} hideNumberInputArrows - The hide input arrows state of the input (for number inputs only)
 */
export function TableInput(props: TableInputProps) {
  const hideNumberInputArrows =
    "[appearance:textfield] [&::-webkit-outer-spin-button]:tw-appearance-none [&::-webkit-inner-spin-button]:tw-appearance-none";

  return (
    <span>
      <input
        disabled={props.disabled}
        maxLength={props.maxLength}
        id={props.id}
        min={props.min}
        type={props.type ? props.type : "text"}
        className={cn(
          props.hideNumberInputArrows && hideNumberInputArrows,
          `tw-border-box tw-h-[36px] tw-w-full tw-cursor-text tw-rounded-lg tw-border-[1px] tw-border-solid tw-border-gray-5 tw-p-[8px] placeholder:tw-overflow-visible placeholder:tw-text-gray-5 hover:tw-border-gray-0 focus:tw-border-[2px] focus:tw-border-blue-2 focus:tw-p-[7px] focus:tw-shadow-[0px_0px_4px_0px_rgba(0,133,222,0.50)] focus:tw-outline-0 1040:tw-px-[4px] focus:1040:tw-px-[3px]`,
          `${props.type == "date" ? (!props.value ? "tw-text-gray-5 focus:tw-text-gray-0" : "tw-text-gray-0") : ""}`,
          props.className,
          props.disabled && "hover:tw-border-gray-5",
        )}
        value={props.value}
        onClick={props.onClick}
        onChange={(e) => {
          props.onChange(e);
        }}
        onFocus={(e) => {
          if (props.onFocus) props.onFocus(e);
        }}
        onBlur={(e) => {
          if (props.onBlur) props.onBlur(e);
        }}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        style={props.overrideStyle}
        data-testid={props.dataTestId}
        aria-invalid={props.ariaInvalid}
        aria-describedby={props.ariaDescribedBy}
        ref={props.innerRef}
      />
      {/* Display if validation fails but box is not in focus */}
      {props.children}
      {props.permanentPlaceholder && (
        <div className="tw-relative">
          <span className="light-grey tw-absolute tw-right-[9px] tw-top-[-29px] tw-select-none tw-opacity-50">
            {props.permanentPlaceholder}
          </span>
        </div>
      )}
    </span>
  );
}

interface TableInputWithXProps {
  id?: string;
  min?: string | number;
  type?: HTMLInputTypeAttribute;
  className?: string;
  value: string;
  setValue?: (value: string) => void;
  customClear?: () => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  placeholder?: string;
  overrideStyle?: CSSProperties;
  dataTestId?: string;
  children?: ReactNode;
  focused?: boolean;
  validate?: () => boolean;
  validationMessage?: string;
  woundWarning?: boolean;
  maxLength?: number;
  innerRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Input component to be used in table forms
 * Can validate values
 */
export function TableInputWithX(props: TableInputWithXProps) {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = props.innerRef ?? internalRef;
  return (
    <div
      tabIndex={-1}
      onClick={() => {
        inputRef.current?.focus();
      }}
      className={cn(
        props.value.length > 0
          ? `hover:tw-pr-[8px] 1040:hover:tw-pr-[4px]`
          : "",
        `tw-border-box tw-group tw-relative tw-mb-0 tw-flex tw-h-[36px] tw-cursor-text tw-items-stretch tw-rounded-lg tw-border-[1px] tw-border-solid tw-border-gray-5 tw-bg-white tw-p-[8px] tw-pr-0 tw-font-normal placeholder:tw-overflow-visible hover:tw-border-gray-0 has-[:focus]:tw-border-[2px] has-[:focus]:tw-border-blue-2 has-[:focus]:tw-p-[7px] has-[:focus]:tw-shadow-[0px_0px_4px_0px_rgba(0,133,222,0.50)] has-[:focus]:tw-outline-0 1040:tw-pl-[4px] has-[:focus]:1040:tw-px-[3px]`,
        props.className,
      )}
    >
      <input
        ref={inputRef}
        id={props.id}
        min={props.min}
        type={props.type ? props.type : "text"}
        className={`tw-peer tw-h-full tw-w-full tw-flex-initial tw-border-0 tw-bg-transparent tw-align-middle tw-outline-none placeholder:tw-text-gray-5 focus:tw-outline-none focus-visible:tw-outline-none has-[:focus]:tw-border-blue-2`}
        value={props.value}
        onChange={(e) => {
          if (props.setValue) props.setValue(e.target.value);
        }}
        onFocus={(e) => {
          if (props.onFocus) props.onFocus(e);
        }}
        onBlur={(e) => {
          if (props.onBlur) props.onBlur(e);
        }}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        style={props.overrideStyle}
        data-testid={props.dataTestId}
        tabIndex={0}
        maxLength={props.maxLength}
      />
      {props.value.length > 0 && (
        <div
          className="tw-invisible tw-flex tw-size-[16px] tw-cursor-pointer tw-items-center tw-justify-center tw-bg-white hover:tw-visible group-hover:tw-visible peer-hover:tw-visible peer-focus:tw-visible"
          onClick={(e) => {
            e.stopPropagation();
            if (props.customClear) {
              props.customClear();
            } else if (props.setValue) {
              props.setValue("");
            }
          }}
        >
          <FontAwesomeIcon
            className="tw-bg-transparent tw-pt-[3px] tw-text-[14px] tw-text-gray-4"
            icon={faXmark}
          />
        </div>
      )}
      {/* Display if validation fails but box is not in focus */}
      {props.children}
    </div>
  );
}

interface InputProps {
  placeholder: string;
  onChange: (value: string) => void;
}

export function TextInput(props: InputProps) {
  return (
    <input
      type="text"
      className="tw-mx-0 tw-my-1 tw-w-full tw-cursor-pointer tw-rounded-md tw-border-[1px] tw-border-solid tw-border-gray-5 tw-bg-white tw-p-4"
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
    ></input>
  );
}
